import React, { useState } from 'react';
import Image from '../../media/portfolio.png';
import PageShell, { Flourish } from '../common/PageShell';
import useIsMobile from '../../hooks/useIsMobile';
import { colors, highlight, motion, radius, space, type } from '../../styles/tokens';

const HomePage = () => {
  const isMobile = useIsMobile();
  const [imgHovered, setImgHovered] = useState(false);

  const styles = {
    heroSection: {
      display: 'flex',
      gap: space[5],
      marginBottom: space[3],
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'center' : 'flex-start'
    },
    contentSection: {
      flex: 1,
      minWidth: 0,
      textAlign: isMobile ? 'center' : 'left'
    },
    imageSection: {
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: space[2],
      order: isMobile ? -1 : 0
    },
    profileImage: {
      width: isMobile ? '140px' : '168px',
      height: isMobile ? '140px' : '168px',
      borderRadius: radius.round,
      objectFit: 'cover',
      border: `3px solid ${imgHovered ? colors.sage : colors.sageLight}`,
      transition: `border-color ${motion.fast} ease`
    },
    imageCaption: {
      ...type.meta,
      fontStyle: 'italic',
      textAlign: 'center'
    },
    divider: {
      width: '48px',
      height: '1px',
      background: colors.sageLight,
      margin: isMobile ? `${space[3]} auto ${space[4]}` : `${space[3]} 0 ${space[5]}`
    },
    prose: {
      ...type.body,
      fontSize: isMobile ? '18px' : '19px',
      lineHeight: 1.75,
      maxWidth: isMobile ? '100%' : '860px',
      marginBottom: space[3]
    },
    term: {
      fontWeight: 600,
      color: colors.inkStrong
    }
  };

  return (
    <PageShell>
      <div style={styles.heroSection}>
        <div style={styles.contentSection}>
          <h1 style={{ ...type.h1(isMobile), marginBottom: space[2] }}>Yanga Booker</h1>
          <p style={{ ...type.subtitle, margin: 0 }}>
            Product
            <Flourish>✦</Flourish>
            CS &amp; Psychology @ Yale
          </p>
          <div style={styles.divider}></div>
        </div>

        <div style={styles.imageSection}>
          <img
            src={Image}
            alt="Yanga Booker"
            style={styles.profileImage}
            onMouseEnter={() => setImgHovered(true)}
            onMouseLeave={() => setImgHovered(false)}
          />
          <p style={styles.imageCaption}>
            Los Angeles, CA<Flourish tone={colors.sageLight}>◆</Flourish>
          </p>
        </div>
      </div>

      <p style={styles.prose}>
        Hi! I'm a sophomore at <em>Yale University</em> studying computer science &amp; psychology,
        and I build <span style={highlight}>products</span>. People always ask how these two majors
        relate. My answer recently has been this.
      </p>

      <p style={styles.prose}>
        <strong style={styles.term}>Computer science</strong> is the study of how computers work
        and think, and <strong style={styles.term}>psychology</strong> is how we learn how people
        work and think.
      </p>

      <p style={styles.prose}>
        Recently I've been building AI tools used inside <em>Yale School of Management</em> and{' '}
        <em>Yale School of the Environment</em>, a consumer iOS app in private beta, and a course
        platform 6,000+ students actually open. I really enjoy being able to synthesize my technical coding knowledge with my learnings about user behavior and human behavior more generally.
      </p>

      <p
        style={{
          ...type.meta,
          fontSize: '15px',
          maxWidth: '860px',
          marginTop: space[5],
          textAlign: isMobile ? 'center' : 'left'
        }}
      >
        Currently shipping<Flourish>→</Flourish>internal AI tools, consumer mobile, on the ground<Flourish>※</Flourish>
      </p>
    </PageShell>
  );
};

export default HomePage;
